import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  Typography,
  FormControl,
  InputLabel,
  Paper,
  Grid,
  useTheme,
  Container,
  Divider,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import ReferralDashboard from './ReferralDashboard';
import { REFERRAL_SERIES_DROPDOWN, SOURCE_COLORS } from './constant';
import { referalSource } from 'store/slices/adminSlice';
import { useAppDispatch } from 'store/hooks';

/* -------------------------------- utils -------------------------------- */

const formatDate = (value?: string | number | null): string => {
  if (!value) return '';

  if (typeof value === 'string' && value.includes('-')) {
    return value; // already yyyy-MM-dd
  }

  const date = new Date(Number(value));
  if (isNaN(date.getTime())) return '';

  // local date (recommended for dashboards)
  return date.toLocaleDateString('en-CA'); // yyyy-MM-dd
};

const normalizeReferral = (referral = ''): string => {
  if (/google/i.test(referral)) return 'google';
  if (/word/i.test(referral)) return 'wordOfMouth';
  if (/pcp/i.test(referral)) return 'pcp';
  if (/social/i.test(referral)) return 'socialMedia';
  if (/counselor/i.test(referral)) return 'therapist';
  if (/hospital/i.test(referral)) return 'hospital';
  if (/insurance/i.test(referral)) return 'insurance';
  if (/school/i.test(referral)) return 'school';
  if (/ai tools/i.test(referral)) return 'aiTools';
  if (/existing/i.test(referral)) return 'migrated';
  if (/account/i.test(referral)) return 'accountmgnt';
  if (/video/i.test(referral)) return 'onlinevideo';
  return 'other';
};




/* ------------------------------- component ------------------------------- */

export default function SourceDashboard() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [location, setLocation] = useState('');
  const [rawReferralData, setRawReferralData] = useState<any[]>([]);

  useEffect(() => {
    dispatch(referalSource({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setRawReferralData(response.data);
        }
      });
  }, [dispatch]);

 const normalizeText = (value = '') =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

 const matchLocation = (
  itemLocation = '',
  selectedLocation = ''
) => {
  if (selectedLocation === 'ALL') return true;

  return normalizeText(itemLocation).includes(
    normalizeText(selectedLocation)
  );
};

const buildPieChartData = (data: any[], location: string) => {
  const counts: Record<string, number> = {};

  data
    .filter(item => matchLocation(item.preferredLocation, location))
    .forEach(item => {
      const key = normalizeReferral(item.referral);
      counts[key] = (counts[key] || 0) + 1;
    });

  return REFERRAL_SERIES_DROPDOWN.map((series, index) => ({
    id: index,
    label: series.label,
    value: counts[series.key] || 0,
    color: SOURCE_COLORS[index],
  })).filter(d => d.value > 0);
};

  /** ✅ normalize dates ONCE */
  const normalizedReferralData = useMemo(
    () =>
      rawReferralData.map(item => ({
        ...item,
        createdDate: formatDate(item.createdDate),
      })),
    [rawReferralData]
  );

  /** ✅ chart data uses normalized data */
  const chartData = useMemo(
    () => buildPieChartData(normalizedReferralData, location),
    [normalizedReferralData, location]
  );

  const total = useMemo(
    () => chartData.reduce((sum, d) => sum + d.value, 0),
    [chartData]
  );


const locations = useMemo(() => {
  return Array.from(
    new Set(
      rawReferralData
        .map(item => item.preferredLocation)
        .filter(
          loc => loc && loc.toLowerCase() !== 'marion'
        )
    )
  );
}, [rawReferralData]);



useEffect(() => {
    if(!location) setLocation('ALL');
  }, [location]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={3}>
        {/* LEFT – Donut Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={10} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
             Referral Sources Distribution
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <FormControl  size="small" sx={{ mb: 2 }}>
              
              <Select
                value={location}
                onChange={e => setLocation(e.target.value)}
              >
                 <MenuItem value='ALL'> All Locations</MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" justifyContent="center">
              <PieChart
                series={[
                  {
                    data: chartData,
                    innerRadius: 70,
                    outerRadius: 120,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    arcLabel: item =>
                      total ? `${((item.value / total) * 100).toFixed(1)}%` : '',
                  },
                ]}
                height={400}
                sx={{
                  '& .MuiPieArcLabel-root': {
                    fill: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    textShadow: '0 0 4px rgba(0,0,0,0.6)',
                  },
                }}
              />
            </Box>

            <Typography align="center" variant="subtitle2" color="text.secondary">
              {location} (Total Referrals: {total})
            </Typography>
          </Paper>
        </Grid>

        {/* RIGHT – Legend */}
        <Grid item xs={12} md={6}>
          <Paper elevation={10} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              {location} Location Referral Source Counts: {total}
            </Typography>

            <Divider sx={{ mb: 2 }} />
    <Box
      display="grid"
      gridTemplateColumns="2fr 1fr 1fr"
      alignItems="center"
      sx={{
        py: 1,
        borderBottom: `1px dashed ${theme.palette.divider}`,
      }}
    >
      <Typography variant="body2" fontWeight={600} textAlign="left">Referral Source</Typography>
      <Typography variant="body2" fontWeight={600} textAlign="center">
        Total Count
      </Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">
        % of Total
      </Typography>
    </Box>
            {chartData.map(item => {
  const percentage = total
    ? ((item.value / total) * 100).toFixed(1)
    : '0';

  return (
    <Box
      key={item.id}
      display="grid"
      gridTemplateColumns="2fr 1fr 1fr"
      alignItems="center"
      sx={{
        py: 1,
        borderBottom: `1px dashed ${theme.palette.divider}`,
      }}
    >
      <Typography variant="body2">{item.label}</Typography>
      <Typography variant="body2" fontWeight={600} textAlign="center">
        {item.value}
      </Typography>
            <Typography
        variant="body2"
        color="text.secondary"
        textAlign="right"
      >
        {percentage}%
      </Typography>
    </Box>
  );
})}

          </Paper>
        </Grid>

        {/* FULL WIDTH – Trend Chart */}
        <Grid item xs={12}>
          <Paper elevation={10} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Referral Source Trends Over Time
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* ✅ Pass normalized data */}
            <ReferralDashboard rawReferralData={normalizedReferralData}  />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
